import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps,
  IconButton,
  Typography,
  Box,
  styled,
  useTheme,
  Card,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GlassCard from "./GlassCard";

interface BaseModalProps extends Omit<DialogProps, "title"> {
  open: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: DialogProps["maxWidth"];
  fullWidth?: boolean;
  showCloseButton?: boolean;
  useGlassCard?: boolean;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.7)"
      : "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  "& .MuiDialog-paper": {
    borderRadius: 16,
    overflow: "hidden",
  },
  // Ensure input fields have proper scroll margin in landscape mode
  "& input, & textarea, & [contenteditable]": {
    "@media (orientation: landscape) and (max-width: 900px)": {
      scrollMarginTop: "80px", // Space for keyboard
      scrollMarginBottom: "20px",
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  position: "relative",
  // Responsive padding for mobile landscape
  [theme.breakpoints.down("sm")]: {
    "@media (orientation: landscape)": {
      padding: theme.spacing(1.5, 2),
    },
  },
  // Additional responsive for very small landscape screens
  "@media (orientation: landscape) and (max-width: 900px)": {
    padding: theme.spacing(1, 1.5),
    "& .MuiTypography-root": {
      fontSize: "0.9375rem",
    },
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}80, transparent)`,
    borderRadius: "2px",
  },
  "& .MuiTypography-root": {
    fontWeight: 700,
    fontSize: "1.25rem",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "0.3px",
    // Responsive font size for mobile landscape
    [theme.breakpoints.down("sm")]: {
      "@media (orientation: landscape)": {
        fontSize: "1rem",
      },
    },
  },
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    color: theme.palette.primary.main,
    transform: "rotate(90deg)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(0),
  "&.MuiDialogContent-root": {
    padding: theme.spacing(0),
  },
  // Responsive for mobile landscape
  [theme.breakpoints.down("sm")]: {
    "@media (orientation: landscape)": {
      maxHeight: "calc(90vh - 140px)",
      overflowY: "auto",
      // Smooth scrolling for better UX
      scrollBehavior: "smooth",
      // Add padding bottom to ensure last input is accessible
      paddingBottom: "20px",
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(0, 0, 0, 0.2)",
        borderRadius: "2px",
      },
    },
  },
  // Additional responsive for very small landscape screens
  "@media (orientation: landscape) and (max-width: 900px)": {
    maxHeight: "calc(90vh - 120px)",
    overflowY: "auto",
    scrollBehavior: "smooth",
    paddingBottom: "20px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.2)",
      borderRadius: "2px",
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  gap: theme.spacing(1.5),
  // Responsive padding for mobile landscape
  [theme.breakpoints.down("sm")]: {
    "@media (orientation: landscape)": {
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
    },
  },
  // Additional responsive for very small landscape screens
  "@media (orientation: landscape) and (max-width: 900px)": {
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(0.75),
    "& button": {
      fontSize: "0.875rem",
      padding: theme.spacing(0.5, 1.5),
      minHeight: "36px",
    },
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}80, transparent)`,
    borderRadius: "2px",
  },
}));

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = "sm",
  fullWidth = true,
  showCloseButton = true,
  useGlassCard = true,
  ...dialogProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dialogContentRef = React.useRef<HTMLDivElement>(null);

  // Handle input focus to prevent keyboard from covering input in landscape mode
  React.useEffect(() => {
    if (!open || !isMobile) return;

    const handleInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Check if target is an input, textarea, or contenteditable element
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.closest("input, textarea, [contenteditable]"))
      ) {
        // Use setTimeout to ensure keyboard is fully shown
        setTimeout(() => {
          // Try to find the actual input element
          const inputElement = target.tagName === "INPUT" || target.tagName === "TEXTAREA"
            ? target
            : (target.closest("input, textarea") as HTMLElement);

          if (inputElement) {
            // Scroll the input into view with smooth behavior
            inputElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });

            // Also try scrolling the dialog content if available
            if (dialogContentRef.current) {
              const inputRect = inputElement.getBoundingClientRect();
              const dialogRect = dialogContentRef.current.getBoundingClientRect();

              // Check if input is below visible area
              if (inputRect.bottom > dialogRect.bottom) {
                const scrollAmount = inputRect.bottom - dialogRect.bottom + 20; // 20px padding
                dialogContentRef.current.scrollBy({
                  top: scrollAmount,
                  behavior: "smooth",
                });
              }
            }
          }
        }, 300); // Wait for keyboard animation
      }
    };

    // Use visualViewport API if available (better for mobile)
    if (window.visualViewport) {
      const handleViewportResize = () => {
        const activeElement = document.activeElement as HTMLElement;
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            activeElement.isContentEditable ||
            activeElement.closest("input, textarea, [contenteditable]"))
        ) {
          setTimeout(() => {
            const inputElement = activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA"
              ? activeElement
              : (activeElement.closest("input, textarea") as HTMLElement);

            if (inputElement) {
              inputElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "nearest",
              });
            }
          }, 100);
        }
      };

      window.visualViewport.addEventListener("resize", handleViewportResize);

      return () => {
        window.visualViewport?.removeEventListener("resize", handleViewportResize);
      };
    } else {
      // Fallback for browsers without visualViewport support
      document.addEventListener("focusin", handleInputFocus, true);

      return () => {
        document.removeEventListener("focusin", handleInputFocus, true);
      };
    }
  }, [open, isMobile]);

  const content = useGlassCard ? (
    <Card sx={{ m: 0, p: 0, border: "none", borderRadius: 0 }}>
      {children}
    </Card>
  ) : (
    children
  );

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          background: useGlassCard ? "transparent" : theme.palette.mode === "dark"
            ? "rgba(18, 18, 18, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: useGlassCard ? "none" : "blur(20px)",
          WebkitBackdropFilter: useGlassCard ? "none" : "blur(20px)",
          boxShadow: useGlassCard
            ? "none"
            : theme.palette.mode === "dark"
              ? "0 24px 48px rgba(0, 0, 0, 0.5)"
              : "0 24px 48px rgba(0, 0, 0, 0.15)",
          position: "relative",
          zIndex: 1300,
          // Responsive for mobile landscape
          ...(isMobile && {
            "@media (orientation: landscape)": {
              maxHeight: "90vh",
              margin: theme.spacing(0.5, 1),
              width: "calc(100% - 16px)",
              maxWidth: "calc(100% - 16px) !important",
            },
          }),
          // Additional responsive for very small landscape screens
          "@media (orientation: landscape) and (max-width: 900px)": {
            maxHeight: "90vh",
            margin: theme.spacing(0.5, 1),
            width: "calc(100% - 16px)",
            maxWidth: "calc(100% - 16px) !important",
          },
          "& > *": {
            position: "relative",
            zIndex: 1300,
          },
        },
      }}
      {...dialogProps}
    >
      <StyledDialogTitle>
        {typeof title === "string" ? (
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        ) : (
          title
        )}
        {showCloseButton && (
          <StyledCloseButton
            aria-label="close"
            onClick={onClose}
          >
            <CloseIcon />
          </StyledCloseButton>
        )}
      </StyledDialogTitle>
      <StyledDialogContent ref={dialogContentRef}>
        {content}
      </StyledDialogContent>
      {footer && (
        <StyledDialogActions>
          {footer}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

export default BaseModal;
